import type { User } from '@/types/milestone.types'

/**
 * Global role constants matching backend definitions
 * @see backend/library/bitvoid_core/schema/roles.py
 */
export const GlobalRoles = {
  RP_ADMIN: 'rp_admin',
  RP_SUPPORT: 'rp_support',
  SYSTEM: 'system', // Reserved for background jobs
} as const

/**
 * Check if user has any of the specified global roles
 * @param user - The current user object (can be null or undefined)
 * @param roles - Array of role strings to check against
 * @returns true if user has at least one of the specified roles
 */
export function hasGlobalRole(user: User | null | undefined, roles: string[]): boolean {
  if (!user?.globalRoles) return false
  return user.globalRoles.some(role => roles.includes(role))
}

/**
 * Check if user can see sandbox environment option
 * Only rp_admin and rp_support can see/select sandbox
 * @param user - The current user object (can be null or undefined)
 * @returns true if user should see the sandbox environment picker
 */
export function canSeeSandbox(user: User | null | undefined): boolean {
  return hasGlobalRole(user, [GlobalRoles.RP_ADMIN, GlobalRoles.RP_SUPPORT])
}

/**
 * Check if user is a Rocket Pricing admin
 * @param user - The current user object (can be null or undefined)
 * @returns true if user has rp_admin role
 */
export function isRpAdmin(user: User | null | undefined): boolean {
  return hasGlobalRole(user, [GlobalRoles.RP_ADMIN])
}

/**
 * Check if user is Rocket Pricing support staff
 * @param user - The current user object (can be null or undefined)
 * @returns true if user has rp_support role
 */
export function isRpSupport(user: User | null | undefined): boolean {
  return hasGlobalRole(user, [GlobalRoles.RP_SUPPORT])
}

/**
 * Check if user has an active subscription plan.
 *
 * Plan is considered present if:
 * - user.plan is not null/undefined
 * - user.plan is not an empty string
 * - user.plan is not an empty object JSON string ('{}')
 *
 * @param user - The current user object (can be null or undefined)
 * @returns true if user has a subscription plan
 *
 * @example
 * ```tsx
 * if (!hasPlan(user)) {
 *   // Redirect to subscription page or show upgrade prompt
 * }
 * ```
 */
export function hasPlan(user: User | null | undefined): boolean {
  if (!user) return false
  if (user.plan === null || user.plan === undefined) return false

  // Handle string representation (JSON string from backend)
  if (typeof user.plan === 'string') {
    const trimmed = user.plan.trim()
    // Empty string or empty object string means no plan
    if (trimmed === '' || trimmed === '{}') return false
  }

  // Has a plan (either PlanData object or non-empty JSON string)
  return true
}
